import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import TableActionButton from "@components/buttons/TableActionButton";
import CopyButton from "@components/buttons/CopyButton";
import DataPill from "@components/pills/DataPill";
import TransactionStatusClass from "@components/classes/TransactionStatusClass";
import { billingServices } from "@services/apis/Billing";
import { ColDef } from "ag-grid-community";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import ConstantFunctions from "../../utils/constants_functions";
import { buildCSV, CSVColumn, downloadCSV } from "../../utils/downloadCSV";
import { BillingHandle } from "./Billing";

type ConsumerInvoice = {
  invoiceId: number;
  invoiceNumber?: string;
  userId: number;
  username?: string;
  serviceId: number;
  service: string;
  direction: string;
  invoiceMonth: string;
  periodStartDate: string;
  periodEndDate: string;
  revenue: string;
  volume: string;
  currencyCode: string;
  status: string;
  dueDate: string | null;
  issuedAt: string;
  createdAt: string;
  updatedAt: string;
  customerName: string;
  country_name: string;
  TaxId: string | null;
};

const ROWS = 100;

const ConsumerInvoices = forwardRef<BillingHandle>((_, ref) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [data, setData] = useState<ConsumerInvoice[]>([]);
  const [offset, setOffset] = useState(0);
  const constantFunctions = new ConstantFunctions();
  const [hasMore, setHasMore] = useState(true);
  const [pdfLoading, setPdfLoading] = useState<{ [key: string]: boolean }>({});
  const transactionStatusClass = new TransactionStatusClass();
  const gridRef = useRef<any>(null);
  const didInitialFetch = useRef(false);
  const inFlight = useRef(false);
  const stopRef = useRef(false); // hard stop
  const pageRequestedRef = useRef(0); // last requested page

  const fetchInvoices = async (nextOffset: number, reset = false) => {
    if (inFlight.current) return;
    if (!reset && stopRef.current) return;

    const page = Math.floor(nextOffset / ROWS) + 1;

    // prevent requesting same/older page again
    if (!reset && page <= pageRequestedRef.current) return;

    inFlight.current = true;
    pageRequestedRef.current = page;

    try {
      setError(null);
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const res = await billingServices.getConsumerInvoices(page, ROWS);

      const rows: ConsumerInvoice[] = res.data.invoices ?? [];
      const p = res.data.pagination;

      // 👇 support both styles:
      // - p.hasMore (your code)
      // - OR infer from returned rows (your debug shows returned: 0)
      const apiHasMore =
        typeof p?.hasMore === "boolean" ? p.hasMore : rows.length === ROWS;

      // ✅ if no more (or backend returned 0), hard stop immediately
      if (!apiHasMore || rows.length === 0) {
        stopRef.current = true;
      }

      setHasMore(apiHasMore && rows.length > 0);
      setData((prev) => (reset ? rows : [...prev, ...rows]));
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      inFlight.current = false;
    }
  };

  // initial load
  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;

    stopRef.current = false;
    pageRequestedRef.current = 0;

    setOffset(0);
    setHasMore(true);
    fetchInvoices(0, true);
  }, []);

  // fetch when offset changes
  useEffect(() => {
    if (!didInitialFetch.current) return;
    if (offset === 0) return;
    fetchInvoices(offset, false);
  }, [offset]);

  // same scroll logic as Expenses/Prime
  const onBodyScroll = useCallback((params: any) => {
    if (stopRef.current || inFlight.current) return;

    const api = params.api;
    const displayedRowCount = api.getDisplayedRowCount();
    const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

    if (
      lastDisplayedRowIndex >= displayedRowCount - 5 &&
      displayedRowCount > 0
    ) {
      setOffset(pageRequestedRef.current * ROWS);
    }
  }, []);

  const exportToCSV = async () => {
    const EXPORT_ROWS = 1000;
    let allRows: ConsumerInvoice[] = [];
    let currentPage = 1;
    let hasMoreData = true;

    while (hasMoreData) {
      const res = await billingServices.getConsumerInvoices(
        currentPage,
        EXPORT_ROWS,
      );
      const rows = res.data.invoices ?? [];
      allRows = [...allRows, ...rows];
      hasMoreData = res.data.pagination?.hasMore ?? false;
      currentPage++;
    }

    const csvColumns: CSVColumn[] = [
      {
        header: "Date",
        field: "issuedAt",
        formatter: (v) => (v ? constantFunctions.dateFormatInDDMMYY(v) : ""),
      },
      { header: "Invoice ID", field: "invoiceId" },
      { header: "Invoice Number", field: "invoiceNumber" },
      { header: "Period", field: "invoiceMonth" },
      { header: "User ID", field: "userId" },
      { header: "Merchant ID", field: "serviceId" },
      { header: "Legal Entity Name", field: "customerName" },
      { header: "Account Name", field: "username" },
      { header: "DBA", field: "customerName" },
      { header: "Location", field: "country_name" },
      { header: "Service", field: "service" },
      { header: "Direction", field: "direction" },
      { header: "Volume", field: "volume" },
      { header: "Amount", field: "revenue" },
      { header: "Currency", field: "currencyCode" },
      { header: "Status", field: "status" },
    ];

    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const csv = buildCSV(allRows, csvColumns);
    downloadCSV(csv, `consumer-invoices-${formattedDate}.csv`);
  };

  useImperativeHandle(ref, () => ({ exportToCSV }));

  const onGridReady = useCallback((params: any) => {
    gridRef.current = params;
  }, []);

  const moneyFormatter = (value: any, currency?: string) => {
    const num = typeof value === "number" ? value : parseFloat(value);
    if (Number.isNaN(num)) return value ?? "-";
    return currency
      ? `${num.toLocaleString()} ${currency}`
      : num.toLocaleString();
  };

  const handlePdfAction = useCallback(
    async (invoiceId: number, isView: boolean) => {
      const key = `${invoiceId}-${isView ? "view" : "download"}`;

      if (pdfLoading[key]) return;

      setPdfLoading((prev) => ({ ...prev, [key]: true }));

      try {
        const { blob: pdfBlob, fileName } =
          await billingServices.getConsumerInvoicePdf({
            invoiceId,
          });

        const blob =
          pdfBlob instanceof Blob
            ? pdfBlob
            : new Blob([pdfBlob], { type: "application/pdf" });

        if (isView) {
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 10000);
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName || `invoice-${invoiceId}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        console.error("Error handling PDF:", e);
      } finally {
        setPdfLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [pdfLoading],
  );

  const handleViewPdf = useCallback(
    (invoiceId: number) => {
      handlePdfAction(invoiceId, true);
    },
    [handlePdfAction],
  );

  const handleDownloadPdf = useCallback(
    (invoiceId: number) => {
      handlePdfAction(invoiceId, false);
    },
    [handlePdfAction],
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "issuedAt",
        width: 120,
        valueFormatter: (p) =>
          p.value ? constantFunctions.dateFormatInDDMMYY(p.value) : "-",
      },

      {
        headerName: "Invoice ID",
        field: "invoiceId",
        width: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="Invoice ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Invoice Number",
        field: "invoiceNumber",
        width: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="Invoice Number copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Period",
        field: "invoiceMonth",
        width: 120,
        filter: "agTextColumnFilter",
      },

      {
        headerName: "User ID",
        field: "userId",
        width: 100,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="User ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Merchant ID",
        field: "serviceId",
        width: 110,
        filter: "agNumberColumnFilter",
      },

      {
        headerName: "Legal Entity Name",
        field: "customerName",
        minWidth: 180,
        wrapText: true,
        autoHeight: true,
      },

      {
        headerName: "Account Name",
        field: "username",
        minWidth: 140,
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "DBA",
        field: "customerName",
        minWidth: 140,
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "Location",
        field: "country_name",
        minWidth: 140,
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "Service",
        field: "service",
        width: 160,
      },

      {
        headerName: "Direction",
        field: "direction",
        width: 110,
        cellRenderer: ({ data }: any) => {
          const categoryStyle = transactionStatusClass.getTransactionStatusIcon(
            data.direction.toLowerCase(),
          );
          return (
            <div
              className={`inline-flex h-[20px] items-center justify-center gap-1 whitespace-nowrap rounded px-[6px] text-[10px] font-medium ${categoryStyle.bgColor}`}
              style={{ color: categoryStyle.textColor }}>
              {data.direction.toUpperCase() || "-"}
            </div>
          );
        },
      },

      {
        headerName: "Volume",
        field: "volume",
        width: 140,
        filter: "agNumberColumnFilter",
        valueFormatter: (p) => moneyFormatter(p.value),
      },

      {
        headerName: "Amount",
        field: "revenue",
        width: 140,
        filter: "agNumberColumnFilter",
        valueFormatter: (p) => moneyFormatter(p.value),
      },

      {
        headerName: "Currency",
        field: "currencyCode",
        width: 95,
      },
      {
        headerName: "PDF",
        field: "pdf",
        width: 190,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const invoiceId = params.data.invoiceId;
          const viewKey = `${invoiceId}-view`;
          const downloadKey = `${invoiceId}-download`;

          return (
            <div className="flex items-center gap-1.5 h-full">
              <TableActionButton
                variant="neutral"
                icon="visibility"
                loading={Boolean(pdfLoading[viewKey])}
                onClick={(event) => {
                  event.stopPropagation();
                  handleViewPdf(invoiceId);
                }}
              >
                View
              </TableActionButton>
              <TableActionButton
                variant="primary"
                icon="download"
                loading={Boolean(pdfLoading[downloadKey])}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDownloadPdf(invoiceId);
                }}
              >
                Download
              </TableActionButton>
            </div>
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        width: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => <DataPill value={params.value} />,
      },
    ],
    [handleDownloadPdf, handleViewPdf, pdfLoading],
  );

  return (
    <div
      className="flex-1 py-4 overflow-hidden"
      style={{ height: "calc(100dvh - 70px)" }}>
      {loading && offset === 0 ? (
        <AgGridSkeleton
          columnDefs={columnDefs}
          style={{ height: "calc(100vh - 150px)" }}
        />
      ) : (
        <div className="flex flex-col h-full">
          <AgGridReactTable
            ref={gridRef}
            rowData={data}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            onBodyScroll={onBodyScroll}
            error={error ?? undefined}
            style={{ height: "calc(100vh - 150px)" }}
            getRowId={(params) => params.data.invoiceId?.toString()}
          />

          {loadingMore && data.length > 0 && (
            <div className="flex items-center justify-center py-4 border-t border-border-subtle">
              <div className="flex items-center space-x-2 text-text-subtle">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary" />
                <span className="text-sm">Loading more invoices...</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

ConsumerInvoices.displayName = "ConsumerInvoices";

export default ConsumerInvoices;
